/**
 * Visual Page Builder for tour pages — drag-and-drop media_blocks editor.
 * Block types: heading, paragraph, image, video, gallery, quote, two_column.
 * Used inside AdminTourEdit; renders the editable list with up/down/delete + per-type editor.
 */
import { useRef } from "react";
import api from "../../lib/api";
import {
  TextT, Paragraph, Image as ImageIcon, FilmStrip, GridFour, Quotes,
  Columns, Plus, Trash, ArrowUp, ArrowDown, UploadSimple,
} from "@phosphor-icons/react";

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: TextT },
  { type: "paragraph", label: "Paragraph", icon: Paragraph },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "video", label: "Video", icon: FilmStrip },
  { type: "gallery", label: "Gallery", icon: GridFour },
  { type: "quote", label: "Quote", icon: Quotes },
  { type: "two_column", label: "Two column", icon: Columns },
];

function newBlock(type) {
  const base = { id: `blk_${Math.random().toString(36).slice(2, 10)}`, type };
  switch (type) {
    case "heading": return { ...base, text: "" };
    case "paragraph": return { ...base, text: "" };
    case "image": return { ...base, src: "", caption: "" };
    case "video": return { ...base, url: "" };
    case "gallery": return { ...base, images: [] };
    case "quote": return { ...base, text: "", attribution: "" };
    case "two_column": return { ...base, image: "", text: "", layout: "image-left" };
    default: return base;
  }
}

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
}

export default function MediaBlocksEditor({ blocks, onChange }) {
  const list = Array.isArray(blocks) ? blocks : [];

  // Supports BOTH value and functional updaters from the parent — so rapid
  // sequential clicks (add-heading then add-paragraph) don't lose updates due
  // to stale closures on the parent's form state.
  const apply = (next) => {
    if (typeof onChange !== "function") return;
    // We try functional update first; if the parent isn't ready for it the value
    // form still works because `next` is computed from the latest `blocks` prop.
    onChange(typeof next === "function" ? next : next);
  };

  const update = (i, patch) => apply((prev) => {
    const src = Array.isArray(prev) ? prev : list;
    return src.map((b, j) => j === i ? { ...b, ...patch } : b);
  });
  const remove = (i) => apply((prev) => {
    const src = Array.isArray(prev) ? prev : list;
    return src.filter((_, j) => j !== i);
  });
  const move = (i, dir) => apply((prev) => {
    const src = Array.isArray(prev) ? prev : list;
    const j = i + dir;
    if (j < 0 || j >= src.length) return src;
    const next = [...src];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const add = (type) => apply((prev) => [...(Array.isArray(prev) ? prev : list), newBlock(type)]);

  return (
    <div data-testid="media-blocks-editor">
      <p className="label-eyebrow text-ms-stone text-xs">Page builder · cinematic blocks rendered on the public tour page below the hero</p>

      {/* Existing blocks */}
      <div className="mt-3 space-y-3">
        {list.map((b, i) => (
          <BlockCard
            key={b.id || i}
            block={b}
            index={i}
            total={list.length}
            onChange={(patch) => update(i, patch)}
            onMove={(dir) => move(i, dir)}
            onRemove={() => remove(i)}
          />
        ))}
        {list.length === 0 && (
          <p className="text-xs text-ms-stone bg-ms-cream/40 border border-dashed border-ms-edge rounded-md p-4">No content blocks yet. Add one below.</p>
        )}
      </div>

      {/* Add block menu */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="mb-add-block">
        <span className="label-eyebrow text-ms-stone text-[10px]">Add block:</span>
        {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
          <button
            key={type}
            type="button"
            onClick={() => add(type)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-ms-edge rounded-md text-ms-stone hover:bg-ms-earth hover:text-ms-cream hover:border-ms-earth transition-colors"
            data-testid={`mb-add-${type}`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function BlockCard({ block, index, total, onChange, onMove, onRemove }) {
  const meta = BLOCK_TYPES.find(b => b.type === block.type) || { label: block.type, icon: Paragraph };
  const Icon = meta.icon;
  return (
    <div className="bg-white border border-ms-edge rounded-md p-4" data-testid={`mb-block-${block.type}-${index}`}>
      <div className="flex items-center justify-between">
        <p className="label-eyebrow text-ms-stone text-[10px] flex items-center gap-2">
          <Icon size={14} className="text-ms-brass" /> {meta.label} <span className="text-ms-edge">·</span> #{index + 1}
        </p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="p-1 text-ms-stone hover:text-ms-earth disabled:opacity-30" data-testid={`mb-move-up-${index}`}><ArrowUp size={14} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="p-1 text-ms-stone hover:text-ms-earth disabled:opacity-30" data-testid={`mb-move-down-${index}`}><ArrowDown size={14} /></button>
          <button type="button" onClick={onRemove} className="p-1 text-ms-stone hover:text-ms-danger" data-testid={`mb-remove-${index}`}><Trash size={14} /></button>
        </div>
      </div>
      <div className="mt-3">
        <BlockBody block={block} onChange={onChange} index={index} />
      </div>
    </div>
  );
}

function BlockBody({ block, onChange, index }) {
  switch (block.type) {
    case "heading":
      return (
        <input
          value={block.text || ""}
          onChange={e => onChange({ text: e.target.value })}
          placeholder="Section heading"
          className="w-full border border-ms-edge rounded-md px-3 py-2 text-sm serif"
          data-testid={`mb-heading-input-${index}`}
        />
      );
    case "paragraph":
      return (
        <textarea
          value={block.text || ""}
          onChange={e => onChange({ text: e.target.value })}
          rows={4}
          placeholder="Write a paragraph… supports blank lines for paragraph breaks."
          className="w-full border border-ms-edge rounded-md px-3 py-2 text-sm"
          data-testid={`mb-paragraph-input-${index}`}
        />
      );
    case "image":
      return <ImageBlock block={block} onChange={onChange} index={index} />;
    case "video":
      return (
        <div className="space-y-2">
          <input
            value={block.url || ""}
            onChange={e => onChange({ url: e.target.value })}
            placeholder="YouTube / Vimeo / .mp4 URL"
            className="w-full border border-ms-edge rounded-md px-3 py-2 text-sm"
            data-testid={`mb-video-url-${index}`}
          />
          <p className="text-xs text-ms-stone">Pastes from YouTube and Vimeo auto-embed.</p>
        </div>
      );
    case "gallery":
      return <GalleryBlock block={block} onChange={onChange} index={index} />;
    case "quote":
      return (
        <div className="space-y-2">
          <textarea
            value={block.text || ""}
            onChange={e => onChange({ text: e.target.value })}
            rows={3}
            placeholder={`"Best evening we've had in St. Petersburg."`}
            className="w-full border border-ms-edge rounded-md px-3 py-2 text-sm serif italic"
            data-testid={`mb-quote-text-${index}`}
          />
          <input
            value={block.attribution || ""}
            onChange={e => onChange({ attribution: e.target.value })}
            placeholder="— Sarah K., Tampa"
            className="w-full border border-ms-edge rounded-md px-3 py-2 text-sm"
            data-testid={`mb-quote-attribution-${index}`}
          />
        </div>
      );
    case "two_column":
      return <TwoColumnBlock block={block} onChange={onChange} index={index} />;
    default:
      return <p className="text-xs text-ms-stone">Unknown block type: {block.type}</p>;
  }
}

function ImageBlock({ block, onChange, index }) {
  const fileRef = useRef(null);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = await uploadFile(f);
    onChange({ src: url });
  };
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-4">
        {block.src && <img src={block.src} alt="" className="w-32 h-24 object-cover rounded-md border border-ms-edge" />}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 text-xs border border-ms-edge rounded-md hover:bg-ms-cream inline-flex items-center gap-1" data-testid={`mb-image-upload-${index}`}>
              <UploadSimple size={12} /> Upload
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} />
            <input
              value={block.src || ""}
              onChange={e => onChange({ src: e.target.value })}
              placeholder="…or paste image URL"
              className="flex-1 border border-ms-edge rounded-md px-3 py-1.5 text-xs"
              data-testid={`mb-image-url-${index}`}
            />
          </div>
        </div>
      </div>
      <input
        value={block.caption || ""}
        onChange={e => onChange({ caption: e.target.value })}
        placeholder="Caption (optional)"
        className="w-full border border-ms-edge rounded-md px-3 py-2 text-sm italic"
        data-testid={`mb-image-caption-${index}`}
      />
    </div>
  );
}

function GalleryBlock({ block, onChange, index }) {
  const fileRef = useRef(null);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const urls = await Promise.all(files.map(uploadFile));
    onChange({ images: [...(block.images || []), ...urls] });
  };
  const removeImg = (i) => onChange({ images: (block.images || []).filter((_, j) => j !== i) });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {(block.images || []).map((g, i) => (
          <div key={i} className="relative group">
            <img src={g} alt="" className="w-20 h-20 object-cover rounded-md border border-ms-edge" />
            <button type="button" onClick={() => removeImg(i)} className="absolute -top-1 -right-1 bg-ms-danger text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">×</button>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} className="w-20 h-20 border-2 border-dashed border-ms-edge rounded-md flex items-center justify-center text-ms-stone hover:border-ms-brass" data-testid={`mb-gallery-add-${index}`}>
          <Plus />
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPick} />
      </div>
      <p className="text-xs text-ms-stone">2–4 images recommended. Renders as a responsive row.</p>
    </div>
  );
}

function TwoColumnBlock({ block, onChange, index }) {
  const fileRef = useRef(null);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = await uploadFile(f);
    onChange({ image: url });
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange({ layout: "image-left" })} className={`px-3 py-1 text-xs rounded-md border ${block.layout !== "image-right" ? "bg-ms-earth text-ms-cream border-ms-earth" : "border-ms-edge text-ms-stone"}`} data-testid={`mb-tc-layout-left-${index}`}>Image left</button>
        <button type="button" onClick={() => onChange({ layout: "image-right" })} className={`px-3 py-1 text-xs rounded-md border ${block.layout === "image-right" ? "bg-ms-earth text-ms-cream border-ms-earth" : "border-ms-edge text-ms-stone"}`} data-testid={`mb-tc-layout-right-${index}`}>Image right</button>
      </div>
      <div className="flex items-start gap-4">
        {block.image && <img src={block.image} alt="" className="w-32 h-24 object-cover rounded-md border border-ms-edge" />}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 text-xs border border-ms-edge rounded-md hover:bg-ms-cream inline-flex items-center gap-1" data-testid={`mb-tc-upload-${index}`}>
              <UploadSimple size={12} /> Upload
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} />
            <input
              value={block.image || ""}
              onChange={e => onChange({ image: e.target.value })}
              placeholder="…or paste image URL"
              className="flex-1 border border-ms-edge rounded-md px-3 py-1.5 text-xs"
            />
          </div>
        </div>
      </div>
      <textarea
        value={block.text || ""}
        onChange={e => onChange({ text: e.target.value })}
        rows={4}
        placeholder="Body text for this column"
        className="w-full border border-ms-edge rounded-md px-3 py-2 text-sm"
        data-testid={`mb-tc-text-${index}`}
      />
    </div>
  );
}
